import { useState } from "react"

import { terminalControlVariants } from "@/components/terminal/control-variants"
import { DateField, parseTypedDate } from "@/components/terminal/date-field"
import { TerminalDialogContent } from "@/components/terminal/dialog-close"
import { Field } from "@/components/terminal/field"
import { terminalDialogSubjectRuleClassName } from "@/components/terminal/surfaces"
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { errorMessage } from "@/lib/error-message"
import type { TerminalFileView } from "@/lib/terminal/file-view"
import { cn } from "@/lib/utils"

import { MastheadAssessment } from "./masthead-assessment"

const buttonClassName = terminalControlVariants({ size: "s", intent: "quiet" })

// The File masthead's one action for a one-time Company: Start monitoring
// opens a confirmation that records the buyer's optional approval date.
export const StartMonitoring = ({
  companyName,
  assessment,
  onStartMonitoring,
}: {
  companyName: string
  assessment: TerminalFileView["assessment"]
  onStartMonitoring: (approvedOn: string | null) => Promise<void>
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [approvedOn, setApprovedOn] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)

  const confirm = async () => {
    const parsed = parseTypedDate(approvedOn)
    if (parsed === undefined) {
      setError("Enter the approval date as M/D/YY, or leave it blank.")
      return
    }
    setIsPending(true)
    setError(null)
    try {
      await onStartMonitoring(parsed)
      setIsOpen(false)
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={buttonClassName}
      >
        Start monitoring
      </button>
      <Dialog
        open={isOpen}
        onOpenChange={(isNextOpen) => {
          setIsOpen(isNextOpen)
          if (!isNextOpen) setError(null)
        }}
      >
        <TerminalDialogContent>
          {/* The subject, exactly as the page's masthead shows it (name,
              square, band word), then the question. One anatomy for every
              confirmation: subject, question, fields, Cancel + verb. */}
          {/* The verb says what happens, so there is no question line. */}
          <DialogTitle className="flex flex-col gap-3">
            <span className="type-display break-words">{companyName}</span>
            <MastheadAssessment assessment={assessment} />
            <span
              className={cn(terminalDialogSubjectRuleClassName, "mt-0.5")}
            />
          </DialogTitle>
          {/* The dialog's words are being worked out with Dhanur (24 Oct);
              until then it says only what the controls say. */}
          <DialogDescription className="sr-only">
            Start monitoring {companyName}.
          </DialogDescription>
          <form
            className="flex flex-col gap-(--terminal-space-dialog)"
            onSubmit={(event) => {
              event.preventDefault()
              void confirm()
            }}
          >
            <Field label="Approved on">
              {({ id }) => (
                <DateField
                  id={id}
                  value={approvedOn}
                  onChange={(text) => {
                    setApprovedOn(text)
                    setError(null)
                  }}
                />
              )}
            </Field>
            {error && (
              <p role="alert" className="type-prose-sm text-signal-bad">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className={terminalControlVariants({
                  size: "l",
                  intent: "quiet",
                })}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className={terminalControlVariants({
                  size: "l",
                  intent: "primary",
                })}
              >
                Start monitoring
              </button>
            </div>
          </form>
        </TerminalDialogContent>
      </Dialog>
    </>
  )
}
